import { Link, useNavigate, useParams } from 'react-router-dom';
import { GoogleIcon } from '../components/GoogleIcon';
import { TopBar } from '../components/TopBar';
import { availablePositions, type PositionDefinition } from '../content/positions';
import { FIELD_WORKFLOW, HVAC_CUSTOMER_PRICE, HVAC_PAY, HVAC_PAY_DETAIL, HVAC_WHAT_YOU_SELL, hvacJobPage, REMOTE_WORKFLOW, WORK_CYCLE } from '../content/hvac';
import { useAuth } from '../state/AuthContext';
import { api, ApiError } from '../api/client';
import { useState } from 'react';
import { routeForFurthestProgress } from '../lib/progressRoutes';
import { availabilityFor, POSITION_DISCONTINUED_MESSAGE, positionLockMessage, useApplicationAvailability, useOpenPositions } from '../lib/applicationAvailability';
import { fillMarkets, useFieldMarkets } from '../lib/fieldMarkets';
import type { ProgressData } from '../state/ProgressContext';

export function JobDetail() {
  const { positionId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const { availability, loading: availabilityLoading } = useApplicationAvailability(Boolean(user) && !user?.isAdmin);
  const { openRoleIds, loading: positionsLoading } = useOpenPositions();
  // Where the position is open is written from the server's own market list (see
  // lib/fieldMarkets.ts), so the posting and the application can never name different places.
  const markets = useFieldMarkets();
  const position = availablePositions.find(({ id }) => id === positionId);

  // A position the admin has closed is not public. An admin can still open its page to check it.
  const closed = Boolean(position && openRoleIds && !openRoleIds.has(position.roleId));
  if (!position || (closed && !user?.isAdmin && !positionsLoading)) {
    return (
      <div className="app-shell">
        <TopBar />
        <main className="job-detail-not-found">
          <h1>{position ? 'This position is not accepting applications' : 'Position not found'}</h1>
          <p>{position ? POSITION_DISCONTINUED_MESSAGE : 'This position is unavailable or the link is incorrect.'}</p>
          <Link to="/">View open positions</Link>
        </main>
      </div>
    );
  }

  const { listing: job } = position;
  const returnTo = `/jobs/${position.id}`;
  const positionState = availabilityFor(availability, position.roleId);
  const lockedMessage = positionLockMessage(position.roleId, availability, openRoleIds);

  async function startApplication() {
    if (!user) return;
    if (user.isAdmin) return navigate('/admin');
    if (lockedMessage) return;
    setStarting(true); setStartError(null);
    try {
      await api.post('/api/progress/position', { roleId: position!.roleId });
      const progress = await api.get<ProgressData>('/api/progress');
      navigate(routeForFurthestProgress(progress));
    } catch (error) {
      setStartError(error instanceof ApiError ? error.message : 'Could not start this application.');
    } finally { setStarting(false); }
  }

  const hvac = position.product === 'HVAC_SPEED_TO_LEAD';

  return (
    <div className="app-shell job-detail-page">
      <TopBar applicationName={job.title} />
      <main className="job-detail-main">
        <Link className="job-detail-back" to="/">&larr; All open positions</Link>
        {closed && user?.isAdmin && <div className="alert warning">Admin view: this position is closed and hidden from applicants.</div>}
        <div className="job-detail-layout">
          <article className="job-detail-content">
            <p className="jobs-eyebrow">KROVANCE CAREERS</p>
            {hvac ? <HvacJobContent position={position} markets={markets} /> : <>
              <h1>{job.title}</h1>
              <div className="job-meta job-detail-meta"><span>{job.location}</span><span>{job.type}</span></div>
              <div className="job-tags">{job.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              {job.compensationSummary && <p className="job-detail-compensation"><strong>Compensation:</strong> {job.compensationSummary}</p>}
              <section>
                <h2>About the opportunity</h2>
                {job.description.map((paragraph) => <p key={paragraph}>{fillMarkets(paragraph, markets)}</p>)}
              </section>
              <section>
                <h2>What you will do</h2>
                <ul>{job.responsibilities.map((item) => <li key={item}>{item}</li>)}</ul>
              </section>
              <section>
                <h2>What we are looking for</h2>
                <ul>{job.qualifications.map((item) => <li key={item}>{fillMarkets(item, markets)}</li>)}</ul>
              </section>
            </>}
          </article>
          <aside className="job-detail-apply-card">
            <h2>Interested in this role?</h2>
            <p>{hvac ? 'No experience needed. The application takes about 10 minutes.' : 'Review the details, then begin the Krovance application when you are ready.'}</p>
            {loading || availabilityLoading || positionsLoading ? (
              <button className="job-detail-cta" type="button" disabled>Checking sign-in&hellip;</button>
            ) : user ? (
              <button className="job-detail-cta" type="button" disabled={starting || (Boolean(lockedMessage) && !user.isAdmin)} title={lockedMessage ?? undefined} onClick={() => void startApplication()}>
                {user.isAdmin ? 'Open dashboard' : lockedMessage ? 'Application unavailable' : starting ? 'Opening application…' : positionState?.started ? 'Continue application' : 'Apply Now'}
              </button>
            ) : (
              <a className="job-detail-cta google" href={`/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`}><GoogleIcon /> Sign in with Google to apply</a>
            )}
            {lockedMessage && !user?.isAdmin && <p className="job-detail-locked-note">{lockedMessage}</p>}
            {!user && !loading && <p className="job-detail-signin-note">Google sign-in is required only when you are ready to apply.</p>}
            {startError && <div className="alert error">{startError}</div>}
          </aside>
        </div>
      </main>
    </div>
  );
}

function HvacJobContent({ position, markets }: { position: PositionDefinition; markets: string[] | null }) {
  const field = position.workMode === 'FIELD';
  const page = hvacJobPage(position.workMode);
  const workflow = field ? FIELD_WORKFLOW : REMOTE_WORKFLOW;
  return <>
    <h1>HVAC Speed-to-Lead Sales Representative</h1>
    <p className="hvac-mode-label">{page.modeLabel}</p>
    {field && <p className="job-summary">{fillMarkets('Open only in {MARKETS_FULL_AND}. You must live in or near one of these areas - you choose yours on the first step of the application.', markets)}</p>}

    <div className="hvac-facts" role="list">
      <div className="hvac-fact" role="listitem"><span>How you get paid</span><strong>{HVAC_PAY}</strong></div>
      <div className="hvac-fact" role="listitem"><span>Employment type</span><strong>Independent contractor / commission-only</strong></div>
      <div className="hvac-fact" role="listitem"><span>Experience</span><strong>No experience required. Training is provided.</strong></div>
      <div className="hvac-fact" role="listitem"><span>What you sell</span><strong>A lead-response system for HVAC businesses</strong></div>
    </div>

    <section>
      <h2>What you sell</h2>
      <p>{HVAC_WHAT_YOU_SELL}</p>
      <p>HVAC companies pay <strong>{HVAC_CUSTOMER_PRICE}</strong>.</p>
    </section>

    <section>
      <h2>What you actually do</h2>
      <ol className="hvac-flow">
        {workflow.map((step, index) => <li key={step}><span className="hvac-flow-number">{index + 1}</span><span>{step}</span></li>)}
      </ol>
      <p>You can message your sales manager any time you have a question or need help.</p>
      <p className="hvac-callout">This is a real {field ? 'door-to-door' : 'phone'} prospecting and sales job. <strong>Most businesses will not buy right away.</strong> You will hear “no” often - the reps who do well keep going and use the feedback they get.</p>
    </section>

    <section>
      <h2>How you get paid</h2>
      <p>{HVAC_PAY_DETAIL}</p>
    </section>

    <section>
      <h2>What a normal lead looks like</h2>
      <ul>{page.normalLead.map((item) => <li key={item}>{item}</li>)}</ul>
    </section>

    <section>
      <h2>What happens after each lead</h2>
      {page.afterLead.map((item) => <p key={item}>{item}</p>)}
      <ol className="hvac-cycle" aria-label="The work cycle">
        {WORK_CYCLE.map((step) => <li key={step}>{step}</li>)}
      </ol>
    </section>

    <section>
      <h2>What we teach you</h2>
      <ul>{page.teach.map((item) => <li key={item}>{item}</li>)}</ul>
    </section>

    <section>
      <h2>What you need</h2>
      <ul>{page.needs.map((item) => <li key={item}>{item}</li>)}</ul>
    </section>

    <section>
      <h2>What happens after you apply</h2>
      <ol>{page.afterApply.map((item) => <li key={item}>{item}</li>)}</ol>
    </section>
  </>;
}
